import React from "react";
import { T, useLanguage } from "../../../context/LanguageContext";

type Shipment = {
  id: string;
  sn: number;
  orderType: string;
  createdAt: string;
  platform: string;
  awbNumber: string;
  referenceNumber: string;
  warehouseName: string;
  routeCode: string;
  shippingCompany: string;
  shippingAwbNumber: string;
  senderCityName: string;
  receiverCityName: string;
  receiverCountryName: string;
  receiverDistrictName: string;
  receiverNationalAddress: string;
  receiverAddress?: string;
  receiverName: string;
  receiverMobile: string;
  receiverEmail?: string;
  paymentType: string;
  codAmount: number;
  status: string;
  updatedAt: string;
  dispatchStatus: string;
  clientName: string;
  outletName?: string;
  senderName?: string;
  olineItems: {
    itemName: string;
    sku: string;
    quantity: number;
    weight: number;
    price: number;
    binLocation: string;
    inventory: number;
    description?: string;
  }[];
};

type Props = {
  data: {
    orders: Shipment[];
  };
  columnsData: any[];
  currentPage: number;
  limitPage: number;
  isCheckboxEnabled: boolean;
  selectedOrders: Shipment[];
  onSelectOrder: (order: Shipment) => void;
  showPackAction?: boolean;
  onPackOrder?: (awb: string) => void;
};

const getStatusBadge = (status: string) => {
  const normStatus = (status || "").toUpperCase();
  switch (normStatus) {
    case "NEW_ORDER":
      return {
        label: "New Order",
        className: "bg-emerald-50 border border-emerald-300 text-emerald-800",
      };
    case "ORDER_GENERATED":
      return {
        label: "Order Generated",
        className: "bg-teal-50 border border-teal-300 text-teal-800",
      };
    case "ORDER_CREATED":
      return {
        label: "Order Created",
        className: "bg-sky-50 border border-sky-300 text-sky-800",
      };
    case "PROCESSING":
      return {
        label: "Processing",
        className: "bg-indigo-50 border border-indigo-300 text-indigo-800",
      };
    case "READY_FOR_PICKUP":
    case "READY_TO_PICK":
      return {
        label: "Ready For Pickup",
        className: "bg-yellow-50 border border-yellow-300 text-yellow-800",
      };
    case "PACKED":
      return {
        label: "Packed",
        className: "bg-violet-50 border border-violet-300 text-violet-800",
      };
    case "DISPATCHED":
      return {
        label: "Dispatched",
        className: "bg-fuchsia-50 border border-fuchsia-300 text-fuchsia-800",
      };
    case "IN_TRANSIT":
      return {
        label: "In Transit",
        className: "bg-cyan-50 border border-cyan-300 text-cyan-800",
      };
    case "OUT_FOR_DELIVERY":
      return {
        label: "Out For Delivery",
        className: "bg-orange-50 border border-orange-300 text-orange-800",
      };
    case "DELIVERED":
      return {
        label: "Delivered",
        className: "bg-lime-50 border border-lime-300 text-lime-800",
      };
    case "UNDELIVERED":
      return {
        label: "Undelivered",
        className: "bg-amber-50 border border-amber-300 text-amber-800",
      };
    case "RETURNED":
      return {
        label: "Returned",
        className: "bg-pink-50 border border-pink-300 text-pink-800",
      };
    case "REVERSE_NEW":
      return {
        label: "Reverse New",
        className: "bg-pink-50 border border-pink-300 text-pink-800",
      };
    case "DELETED":
      return {
        label: "Deleted",
        className: "bg-rose-50 border border-rose-300 text-rose-800",
      };
    case "FAILED":
      return {
        label: "Failed",
        className: "bg-red-50 border border-red-300 text-red-800",
      };
    case "LOST":
      return {
        label: "Lost",
        className: "bg-stone-50 border border-stone-300 text-stone-700",
      };
    case "DRAFT":
      return {
        label: "Draft",
        className: "bg-zinc-50 border border-zinc-300 text-zinc-700",
      };
    case "CANCELLED":
      return {
        label: "Cancelled",
        className: "bg-slate-50 border border-slate-300 text-slate-700",
      };
    case "RECEIVE_IN_WAREHOUSE":
      return {
        label: "Receive In Warehouse",
        className: "bg-blue-50 border border-blue-300 text-blue-800",
      };
    case "PICKUP_GENERATED":
      return {
        label: "Pickup Generated",
        className: "bg-green-50 border border-green-300 text-green-800",
      };
    case "RETURN_TO_SHIPPER":
      return {
        label: "Return To Shipper",
        className: "bg-rose-100 border border-rose-300 text-rose-800",
      };
    case "FORWARD_TO_3PL":
      return {
        label: "Forward To 3PL",
        className: "bg-purple-100 border border-purple-300 text-purple-800",
      };
    case "DISPATCHED_TO_3PL":
      return {
        label: "Dispatched To 3PL",
        className: "bg-violet-100 border border-violet-300 text-violet-800",
      };
    case "PICKUP_COLLECTED":
      return {
        label: "Pickup Collected",
        className: "bg-teal-100 border border-teal-300 text-teal-800",
      };
    case "PICKLIST_GENERATED":
      return {
        label: "Picklist Generated",
        className: "bg-indigo-100 border border-indigo-300 text-indigo-800",
      };
    case "PICKED":
      return {
        label: "Picked",
        className: "bg-sky-100 border border-sky-300 text-sky-800",
      };
    case "RECEIVE_INBOUND":
      return {
        label: "Receive Inbound",
        className: "bg-cyan-100 border border-cyan-300 text-cyan-800",
      };
    case "SHELVE":
      return {
        label: "Shelve",
        className: "bg-emerald-100 border border-emerald-300 text-emerald-800",
      };
    case "PENDING":
      return {
        label: "Pending",
        className: "bg-amber-100 border border-amber-300 text-amber-800",
      };
    case "PENDING_STOCK":
      return {
        label: "Pending Stock",
        className: "bg-orange-100 border border-orange-300 text-orange-800",
      };
    default:
      const label = status
        ? status
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(" ")
        : "Unknown";
      return {
        label,
        className: "bg-slate-50 border border-slate-200 text-slate-600",
      };
  }
};

const AllOrderTable: React.FC<Props> = ({
  data,
  columnsData,
  selectedOrders,
  onSelectOrder,
  currentPage,
  limitPage,
  showPackAction,
  onPackOrder,
}) => {
  const { t } = useLanguage();
  const maxStatusWidth = React.useMemo(() => {
    if (!data?.orders?.length) return "100px";
    const maxLen = data.orders.reduce((max, item) => {
      const label = getStatusBadge(item.status).label || "";
      return label.length > max ? label.length : max;
    }, 0);
    const calculatedWidth = Math.max(80, maxLen * 7.2 + 24);
    return `${calculatedWidth}px`;
  }, [data?.orders]);

  const isColShow = (val: string) => {
    if (!columnsData || columnsData.length === 0) return true;
    return columnsData.find((col) => col.value === val)?.isShow !== false;
  };

  const formatDate = (dateString: string) => {
    const d = new Date(dateString);
    const months = [
      t("Jan"), t("Feb"), t("Mar"), t("Apr"),
      t("May"), t("Jun"), t("Jul"), t("Aug"),
      t("Sep"), t("Oct"), t("Nov"), t("Dec"),
    ];
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  };

  return (
    <div className="w-full max-w-full bg-[#F7F6F2]/30 rounded-3xl overflow-hidden shadow-xs border border-slate-100/80">
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse bg-white table-auto">
          <thead className="text-[#131327]/70 border-b border-dashed border-gray-300 sticky top-0 z-20 bg-[#FCFDF8]">
            <tr className="text-center text-[11px] font-semibold tracking-wider uppercase select-none">
              {/* Checkbox */}
              <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200 text-center w-[30px] min-w-[30px]">
                <div className="flex items-center justify-center">
                  <label className="relative flex items-center justify-center cursor-pointer h-4 w-4">
                    <input
                      type="checkbox"
                      checked={
                        data.orders.length > 0 &&
                        selectedOrders.length === data.orders.length
                      }
                      onChange={(e) => {
                        const checked = e.target.checked;
                        data.orders.forEach((order) => {
                          const isSelected = selectedOrders.some((o) => o.id === order.id);
                          if (checked && !isSelected) {
                            onSelectOrder(order);
                          } else if (!checked && isSelected) {
                            onSelectOrder(order);
                          }
                        });
                      }}
                      className="peer h-4 w-4 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] bg-white checked:bg-[#FBD037] transition-all"
                    />
                    <span className="absolute text-[10px] font-bold text-white peer-checked:opacity-100 opacity-0 pointer-events-none">
                      ✓
                    </span>
                  </label>
                </div>
              </th>
              {isColShow("sn") && <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200 text-center w-[30px] min-w-[30px]"><T>S/N</T></th>}
              {isColShow("tag") && <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200 w-[50px] min-w-[50px]"><T>TAG</T></th>}
              {isColShow("createdDate") && <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200 w-[80px] min-w-[80px] whitespace-nowrap"><T>DATE</T></th>}
              {isColShow("platform") && <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200 w-[65px] min-w-[65px]"><T>PLATFORM</T></th>}
              {isColShow("awbRef") && <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200 w-[100px] min-w-[100px]"><T>AWB & REF</T></th>}
              {isColShow("warehouse") && <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200 w-[110px] min-w-[110px]"><T>WAREHOUSE</T></th>}
              {isColShow("route") && <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200 w-[70px] min-w-[70px]"><T>ROUTE</T></th>}
              {isColShow("shippingDetails") && <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200 w-[110px] min-w-[110px]"><T>3PL SHIPPING DETAILS</T></th>}
              {isColShow("origin") && <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200 w-[90px] min-w-[90px]"><T>ORIGIN</T></th>}
              {isColShow("destination") && <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200 w-[120px] min-w-[120px]"><T>DESTINATION</T></th>}
              {isColShow("receiverDetails") && <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200 w-[100px] min-w-[100px]"><T>RECEIVER DETAILS</T></th>}
              {isColShow("paymentInfo") && <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200 w-[75px] min-w-[75px]"><T>PAYMENT INFO</T></th>}
              {isColShow("merchant") && <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200 w-[105px] min-w-[105px]"><T>MERCHANT</T></th>}
              {isColShow("status") && <th className="px-2 pt-3 pb-2 border-r border-dashed border-gray-200 text-center w-[85px] min-w-[85px]"><T>STATUS</T></th>}
              {isColShow("actions") && <th className="px-2 pt-3 pb-2 text-center w-[100px] min-w-[120px]"><T>ACTIONS</T></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 font-medium text-slate-700 text-[13px]">
            {data.orders.map((item, idx) => {
              const isSelected = selectedOrders.some((o) => o.id === item.id);

              return (
                <React.Fragment key={`${item.id}-${item.paymentType || idx}`}>
                  {/* Standard Row */}
                  <tr
                    className={`hover:bg-slate-50/50 transition duration-100 ${isSelected ? "bg-amber-50/20" : ""
                      }`}
                  >
                    {/* Checkbox Cell */}
                    <td className="py-2 px-1 text-center">
                      <label className="relative flex items-center justify-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => onSelectOrder(item)}
                          className="peer h-4 w-4 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] bg-white checked:bg-[#FBD037] transition-all"
                        />
                        <span className="absolute text-[10px] font-bold text-white peer-checked:opacity-100 opacity-0 pointer-events-none">
                          ✓
                        </span>
                      </label>
                    </td>
                    {/* SN */}
                    {isColShow("sn") && (
                      <td className="py-2 px-1 text-slate-400 font-bold text-center">
                        {(currentPage - 1) * limitPage + (idx + 1)}
                      </td>
                    )}

                    {/* Tag */}
                    {isColShow("tag") && (
                      <td className="py-2 px-1">
                        <span
                          className={`px-1.5 py-0.5 rounded-md text-[10px] font-black tracking-wide border uppercase ${item.orderType === "FM"
                            ? "bg-sky-50 border-sky-200 text-sky-700"
                            : "bg-purple-50 border-purple-200 text-purple-700"
                            }`}
                        >
                          {item.orderType}
                        </span>
                      </td>
                    )}

                    {/* Created Date */}
                    {isColShow("createdDate") && (
                      <td className="py-2 px-1 whitespace-nowrap">
                        <div className="text-slate-800 font-extrabold text-[12px] whitespace-nowrap">
                          {formatDate(item.createdAt)}
                        </div>
                      </td>
                    )}

                    {/* Platform */}
                    {isColShow("platform") && (
                      <td className="py-2 px-1">
                        <span className="bg-slate-50 border border-slate-200 text-slate-600 px-2 py-0.5 rounded-full text-[11px] font-bold tracking-wide shadow-xs whitespace-nowrap">
                          {item.platform}
                        </span>
                      </td>
                    )}

                    {/* AWB No & Ref */}
                    {isColShow("awbRef") && (
                      <td className="py-2 px-1 space-y-0.5">
                        <div className="text-slate-900 font-extrabold text-[12px] leading-none whitespace-nowrap">
                          {item.awbNumber}
                        </div>
                        <div className="text-slate-400 text-[11px] font-bold whitespace-nowrap">
                          <T>Ref:</T> <span className="font-semibold text-slate-500">{item.referenceNumber}</span>
                        </div>
                      </td>
                    )}

                    {/* Warehouse */}
                    {isColShow("warehouse") && (
                      <td className="py-2 px-1 text-center">
                        <div
                          className="text-slate-800 font-semibold text-[12px] leading-tight truncate"
                          data-tooltip={item.platform === "OUTLET" ? item.outletName || item.warehouseName : item.warehouseName}
                        >
                          {item.platform === "OUTLET" ? item.outletName || item.warehouseName : item.warehouseName}
                        </div>
                      </td>
                    )}

                    {/* Route */}
                    {isColShow("route") && (
                      <td className="py-2 px-1 text-center">
                        {item.routeCode ? (
                          <span className="bg-[#FBD037]/20 border border-[#FBD037]/45 text-[#856608] px-1.5 py-0.5 rounded-md text-[10px] font-black inline-flex items-center max-w-full truncate" data-tooltip={item.routeCode}>
                            {item.routeCode}
                          </span>
                        ) : null}
                      </td>
                    )}

                    {/* 3PL Shipping Details */}
                    {isColShow("shippingDetails") && (
                      <td className="py-2 px-1 space-y-1 text-center">
                        <div className="flex items-center gap-1 text-slate-800 text-[12px] font-bold truncate" data-tooltip={item.shippingCompany}>
                          <span>{item.shippingCompany}</span>
                        </div>
                        <div className="text-slate-400 text-[10.5px] font-semibold leading-none font-mono truncate" data-tooltip={item.shippingAwbNumber}>
                          {item.shippingAwbNumber}
                        </div>
                      </td>
                    )}

                    {/* Origin */}
                    {isColShow("origin") && (
                      <td className="py-2 px-1 space-y-0.5 text-center">
                        {(() => {
                          const senderCityStr = item.senderCityName || "";
                          const originCity = senderCityStr.split(" ")[0];
                          const originWhse = senderCityStr.split(" ").slice(1).join(" ");
                          return (
                            <>
                              <div className="flex items-center gap-1 text-[12px] font-extrabold text-slate-800 truncate" data-tooltip={originCity}>
                                <span>{originCity}</span>
                              </div>
                              {originWhse && (
                                <div className="text-[10px] text-slate-400 font-bold truncate" data-tooltip={originWhse}>
                                  {originWhse}
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </td>
                    )}

                    {/* Destination */}
                    {isColShow("destination") && (
                      <td className="px-2 py-3 min-w-0 max-w-0 overflow-hidden">
                        {/* City */}
                        <div
                          className="text-xs font-bold text-[#212121] truncate"
                          data-tooltip={`${item.receiverCityName}${item.receiverCountryName ? `, ${item.receiverCountryName}` : ""}`}
                        >
                          {item.receiverCityName}
                          {item.receiverCountryName ? `, ${item.receiverCountryName}` : ""}
                        </div>

                        {/* District + Short Address */}
                        <div className="text-[9px] text-slate-400 font-bold flex items-center gap-1 mt-1">
                          <span
                            className="truncate"
                            data-tooltip={`Dist: ${item.receiverDistrictName}`}
                          >
                            Dist: {item.receiverDistrictName}
                          </span>

                          {item.receiverNationalAddress && (
                            <span
                              className="border border-slate-200 bg-slate-50 text-slate-500 px-1 rounded font-mono text-[9px] max-w-[80px]"
                              data-tooltip={item.receiverNationalAddress}
                            >
                              {item.receiverNationalAddress}
                            </span>
                          )}
                        </div>
                      </td>
                    )}

                    {/* Receiver Details */}
                    {isColShow("receiverDetails") && (
                      <td className="py-2 px-1 space-y-0.5 min-w-0 max-w-0 overflow-hidden text-center">
                        <div className="text-[12px] text-slate-800 font-extrabold leading-tight truncate" data-tooltip={item.receiverName}>
                          <span>{item.receiverName}</span>
                        </div>
                        <div className="text-[10.5px] text-slate-400 font-bold leading-tight truncate" data-tooltip={item.receiverMobile}>
                          {item.receiverMobile}
                        </div>
                      </td>
                    )}

                    {/* Payment Info */}
                    {isColShow("paymentInfo") && (
                      <td className="py-2 px-1 text-center">
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-black tracking-wider uppercase border inline-block whitespace-nowrap ${item.paymentType === "COD"
                            ? "bg-amber-50 border-amber-200 text-amber-800"
                            : "bg-emerald-50 border-emerald-200 text-emerald-800"
                            }`}
                        >
                          {item.paymentType === "COD" ? `${t("COD")}: ${item.codAmount ?? 0}` : t("CC: PAID")}
                        </span>
                      </td>
                    )}

                    {/* Merchant */}
                    {isColShow("merchant") && (
                      <td className="py-2 px-1 text-center">
                        <span
                          className="text-[12px] font-black text-[#856608] block truncate"
                          data-tooltip={item.platform === "OUTLET" ? item.senderName || item.clientName : item.clientName}
                        >
                          {item.platform === "OUTLET" ? item.senderName || item.clientName : item.clientName}
                        </span>
                      </td>
                    )}

                    {/* Status badge */}
                    {isColShow("status") && (
                      <td className="py-2 px-1 text-center">
                        {(() => {
                          const badge = getStatusBadge(item.status);
                          return (
                            <span
                              style={{ width: maxStatusWidth }}
                              className={`border py-0.5 rounded-full text-[11px] font-extrabold tracking-wide inline-block text-center px-2 truncate max-w-full align-middle shadow-2xs ${badge.className}`}
                              data-tooltip={t(badge.label)}
                            >
                              <T>{badge.label}</T>
                            </span>
                          );
                        })()}
                      </td>
                    )}

                    {/* Actions column */}
                    {isColShow("actions") && (
                      <td className="py-2 px-1 text-center">
                        <div className="flex items-center justify-center gap-1.5 flex-wrap">
                          {showPackAction && (
                            <button
                              onClick={() => onPackOrder && onPackOrder(item?.awbNumber)}
                              className="bg-slate-900 hover:bg-slate-800 text-[#FBD037] text-[11px] font-black px-3.5 py-1.5 mx-0.5 rounded-md shadow-sm transition duration-150 tracking-wide whitespace-nowrap"
                            >
                              <T>Pack Order</T>
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>

                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AllOrderTable;
